import { Button, type ButtonVariant } from "@design";
import { Play } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowResponse } from "@/api";
import { RunFlowDialog } from "./RunFlowDialog";

/** "Run" for a workflow: opens the run dialog. Anyone who can see a workflow can run it. */
export function RunFlowButton({
  flow,
  variant = "primary",
  runPath,
  unsavedSteps = false,
}: Readonly<{
  flow: FlowResponse;
  variant?: ButtonVariant;
  runPath?: (flowUuid: string, runUuid: string) => string;
  /** The Steps editor has unsaved edits; a run would use the saved steps instead. */
  unsavedSteps?: boolean;
}>) {
  const { t } = useTranslation("workflows");
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const noSteps = (flow.steps ?? []).length === 0;
  let blockedReason: string | undefined;
  if (unsavedSteps) blockedReason = t("runDialog.unsavedSteps");
  else if (noSteps) blockedReason = t("runDialog.noSteps");
  return (
    <>
      <Button
        variant={variant}
        size="sm"
        leadingIcon={<Play size={14} aria-hidden />}
        disabled={blockedReason !== undefined}
        title={blockedReason}
        onClick={() => setOpen(true)}
      >
        {t("runs.run")}
      </Button>
      {open && <RunFlowDialog flow={flow} open onClose={close} runPath={runPath} />}
    </>
  );
}
